import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CInput, CLoader } from '../../components/common';
import { FiSearch } from 'react-icons/fi';
import { IoLayersOutline, IoBusinessOutline, IoTimeOutline, IoStarOutline } from 'react-icons/io5';
import { fetchBuckets, BucketSummary } from '../../api/buckets';
import { bucketCategories, BucketCategory } from '../../data/bucketsData';

const categoryIcons: Record<BucketCategory, any> = {
  functional: IoLayersOutline,
  sectoral: IoBusinessOutline,
  historical: IoTimeOutline,
  important: IoStarOutline,
};

const Buckets: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<BucketCategory>('functional');

  // API state
  const [apiBuckets, setApiBuckets] = useState<BucketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch buckets from API
  useEffect(() => {
    const loadBuckets = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchBuckets();
        setApiBuckets(data);
      } catch (err) {
        console.error('Failed to fetch buckets from API:', err);
        setError('Failed to load buckets. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadBuckets();
  }, []);

  // Filter buckets by category and search
  const filteredBuckets = useMemo(() => {
    let buckets = apiBuckets
      .filter((b) => b.bucket_category === selectedCategory)
      .map((b) => ({
        id: b.bucket_name.toLowerCase().replace(/\s+/g, '-'),
        title: b.bucket_name,
        description: `${b.stock_count} companies in this bucket`,
        companyCount: b.stock_count,
        category: b.bucket_category as BucketCategory,
      }));

    if (searchText.trim()) {
      const lowerQuery = searchText.toLowerCase();
      buckets = buckets.filter(
        (bucket) =>
          bucket.title.toLowerCase().includes(lowerQuery) ||
          bucket.description.toLowerCase().includes(lowerQuery)
      );
    }

    return buckets;
  }, [selectedCategory, searchText, apiBuckets]);

  // Get category counts
  const categoryCounts = useMemo(() => {
    return bucketCategories.reduce((acc, cat) => {
      acc[cat.id] = apiBuckets.filter((b) => b.bucket_category === cat.id).length;
      return acc;
    }, {} as Record<BucketCategory, number>);
  }, [apiBuckets]);

  const totalBuckets = apiBuckets.length;

  const handleBucketClick = (bucketName: string) => {
    navigate(`/buckets/${encodeURIComponent(bucketName)}`);
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    fetchBuckets()
      .then((data) => setApiBuckets(data))
      .catch((err) => {
        console.error('Failed to fetch buckets:', err);
        setError('Failed to load buckets. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Header */}
        <div className="mb-4">
          <CText type="B20" color={colors.textColor}>
            Buckets
          </CText>
          <CText type="r14" color={colors.grayScale500} style={{ marginTop: 4 }}>
            {totalBuckets} buckets across {bucketCategories.length} categories
          </CText>
        </div>

        {/* Search */}
        <div className="mb-4">
          <CInput
            value={searchText}
            onChange={setSearchText}
            placeholder="Search buckets..."
            insideLeftIcon={() =>
              React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })
            }
          />
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          {bucketCategories.map((category) => {
            const IconComponent = categoryIcons[category.id];
            const isSelected = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                className="flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors"
                style={{
                  backgroundColor: isSelected ? colors.primary : colors.inputBackground,
                  color: isSelected ? colors.white : colors.grayScale400,
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                {React.createElement(IconComponent as any, { size: 16 })}
                <span className="text-sm font-medium">{category.label}</span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : colors.borderColor,
                    color: isSelected ? colors.white : colors.grayScale500,
                  }}
                >
                  {categoryCounts[category.id] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <CLoader />
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="text-center py-12">
            <CText type="M16" color={colors.alertColor}>
              {error}
            </CText>
            <button
              className="mt-4 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary, color: colors.white }}
              onClick={handleRetry}
            >
              Retry
            </button>
          </div>
        )}

        {/* Buckets Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBuckets.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <CText type="M16" color={colors.grayScale500}>
                  No buckets found
                </CText>
              </div>
            ) : (
              filteredBuckets.map((bucket) => (
                <div
                  key={bucket.id}
                  className="p-4 rounded-xl border cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                  style={{
                    backgroundColor: colors.inputBackground,
                    borderColor: colors.borderColor,
                  }}
                  onClick={() => handleBucketClick(bucket.title)}
                >
                  {/* Bucket Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: colors.primary + '20' }}
                    >
                      {React.createElement(categoryIcons[bucket.category] as any, {
                        size: 20,
                        color: colors.primary,
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <CText type="M14" color={colors.textColor} style={{ marginBottom: 4 }}>
                    {bucket.title}
                  </CText>

                  {/* Description */}
                  <CText
                    type="r12"
                    color={colors.grayScale500}
                    style={{
                      marginBottom: 12,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {bucket.description}
                  </CText>

                  {/* Company Count */}
                  <div
                    className="flex items-center justify-between pt-3"
                    style={{ borderTop: `1px solid ${colors.borderColor}` }}
                  >
                    <CText type="r12" color={colors.grayScale500}>
                      Companies
                    </CText>
                    <div className="flex items-center gap-1">
                      <CText type="B14" color={colors.primary}>
                        {bucket.companyCount.toLocaleString()}
                      </CText>
                      <CText type="r12" color={colors.grayScale500}>
                        stocks
                      </CText>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Summary Footer */}
        {!loading && !error && (
          <div
            className="mt-6 p-4 rounded-xl text-center"
            style={{ backgroundColor: colors.inputBackground }}
          >
            <CText type="r14" color={colors.grayScale500}>
              Showing {filteredBuckets.length} buckets in{' '}
              <span style={{ color: colors.primary, fontWeight: 600 }}>
                {bucketCategories.find((c) => c.id === selectedCategory)?.label}
              </span>
            </CText>
          </div>
        )}
      </div>
    </div>
  );
};

export default Buckets;
